import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Grid, Input, Row, Space, Tooltip, message } from 'antd';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import shallow from 'zustand/shallow';

import { CampaignForm } from '../../components/CampaignForm';
import { PreviewCard } from '../../components/PreviewCard';
import { useCampaignForm } from '../../hooks';
import { useAuthStore, useCampaignsStore } from '../../stores';
import { Campaign, ICampaignFormReturnValue, Template } from '../../typings';
import { config, parseCustomText } from '../../utils';
import { Page } from '../components/Page';

interface LocationState {
  selectedTemplate: Template;
}

export const AddCampaignPage = () => {
  const { t } = useTranslation();
  const breakpoints = Grid.useBreakpoint();
  const { state } = useLocation<LocationState>();

  const [selectedTemplate, setSelectedTemplate] = React.useState(state?.selectedTemplate);
  const [createdCampaign, setCreatedCampaign] = React.useState<Campaign>();
  const user = useAuthStore(({ user }) => user);
  const { addCampaign, updateCampaign } = useCampaignsStore((store) => store, shallow);
  const {
    form,
    previewForm,
    fetchTemplatesLoading,
    onCopyLoading,
    updatePreviewLoading,
    codeText,
    previewUrl,
    options,
    onValuesChange,
    handleSelectScroll,
    createCodeText,
    handleOnCopy,
    updatePreview,
  } = useCampaignForm({
    selectedTemplate,
    onSelectedTemplateChange: setSelectedTemplate,
  });

  const [{ loading: onFinishLoading, error: onFinishError }, handleOnFinish] = useAsyncFn(
    async (values: ICampaignFormReturnValue) => {
      if (!user?.attributes?.sub) return;

      const customFields = values.customText ? parseCustomText(values.customText) : [];

      if (!createdCampaign) {
        const campaign = await addCampaign({ ...values, customText: customFields }, user.attributes.sub);

        setCreatedCampaign(campaign);
        message.success(t('CREATE_CAMPAIGN_SUCCESS_MESSAGE', { campaignName: values.name }));
      } else {
        setCreatedCampaign(await updateCampaign(createdCampaign.id, { ...values, customText: customFields }));
        message.success(t('UPDATE_CAMPAIGN_SUCCESS_MESSAGE', { campaignName: values.name }));
      }

      if (selectedTemplate?.textLayers?.length) {
        updatePreview();
      }
    },
    [createdCampaign, user, addCampaign, updateCampaign, updatePreview, setCreatedCampaign]
  );

  const handleCampaignCodeCopy = async () => {
    createdCampaign && (await handleOnCopy(createdCampaign));
  };

  React.useEffect(() => {
    if (createdCampaign) createCodeText(createdCampaign);
  }, [createCodeText, createdCampaign]);

  React.useEffect(() => {
    if (onFinishError) message.error(onFinishError.message);
  }, [onFinishError]);

  React.useEffect(() => {
    const customText: Record<string, string> = {};

    selectedTemplate?.textLayers?.forEach((textLayer) => {
      if (textLayer.defaultText) customText[textLayer.name] = textLayer.defaultText;
    });

    form.setFieldsValue({
      templateId: selectedTemplate?.id,
      customText,
    });

    updatePreview();
  }, [form, selectedTemplate, updatePreview]);

  const howtoLink = config.HOWTO_LINK ? (
    <Tooltip title={t('HOWTO') as string}>
      <a href={config.HOWTO_LINK} target="_blank" rel="noopener noreferrer">
        <QuestionCircleOutlined />
      </a>
    </Tooltip>
  ) : undefined;

  return (
    <Page title={t('ADD_CAMPAIGN_PAGE_TITLE') as string}>
      <Row gutter={32}>
        <Col span={breakpoints.lg ? 15 : breakpoints.md ? 18 : 24} order={breakpoints.lg ? 1 : 2}>
          <Space style={{ width: '100%' }} size="large" direction="vertical">
            <Card>
              <CampaignForm
                form={form}
                loading={onFinishLoading}
                selectLoading={fetchTemplatesLoading}
                options={options}
                layout="vertical"
                textLayers={selectedTemplate?.textLayers}
                onFinish={handleOnFinish}
                onSelectScroll={handleSelectScroll}
                onValuesChange={onValuesChange}
              />
            </Card>
            <Card
              title={
                <>
                  {t('COPY_URL_CARD_TITLE')} {howtoLink}
                </>
              }
            >
              <Space style={{ width: '100%' }} size="large" direction="vertical">
                <Form.Item help={t('COPY_URL_TEXTAREA_HELP')}>
                  <Input.TextArea
                    value={codeText}
                    rows={3}
                    placeholder={t('COPY_URL_TEXTAREA_PLACEHOLDER') as string}
                    readOnly
                  />
                </Form.Item>
                <Button type="primary" onClick={handleCampaignCodeCopy} loading={onCopyLoading} disabled={!codeText}>
                  {t('COPY_BUTTON')}
                </Button>
              </Space>
            </Card>
          </Space>
        </Col>
        <Col
          span={breakpoints.lg ? 9 : breakpoints.md ? 12 : 24}
          order={breakpoints.lg ? 2 : 1}
          style={{ marginBottom: '24px' }}
        >
          <PreviewCard
            form={previewForm}
            loading={updatePreviewLoading}
            onFinish={updatePreview}
            src={previewUrl || selectedTemplate?.poster}
            attribution={selectedTemplate?.attribution}
          />
        </Col>
      </Row>
    </Page>
  );
};
